/**
 * EVO — kundalik ish ekranlarining URL holati.
 *
 * `list-search.ts` va `report-search.ts` bilan bir xil sabab va bir xil
 * qoidalar:
 *   • URL ISHONCHSIZ KIRISH — har maydon tozalanadi (`?page=-5`,
 *     `?status=DROP TABLE` ekranni buzmaydi, default'ga tushadi);
 *   • DEFAULT qiymatlar URL ga YOZILMAYDI — manzil qisqa va ulashsa bo'ladigan
 *     bo'lib qoladi;
 *   • oq ro'yxatlar SERVERDA takrorlanmaydi: `sortBy` tekshirilmaydi, chunki
 *     yagona haqiqiy ro'yxat u yerda va noto'g'ri maydon 400 qaytaradi.
 *
 * Bu ekranlarda URL ayniqsa muhim: omborchi «mana bu SKU ning daftari» degan
 * havolani buxgalterga tashlaydi va u AYNAN o'sha satrlarni ko'radi. Hisob
 * bo'yicha bahs shu havoladan boshlanadi.
 */
import { SALES_ORDER_STATUS, type SalesOrderStatus } from './sales';
import { INVENTORY_TXN_TYPES, type InventoryTxnType } from './inventory';
import { isIsoDate } from './period';
import type { SortDir } from './products';

/** Musbat butun son yoki `undefined` — ID va sahifa uchun umumiy tozalash. */
function positiveInt(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function sortDir(value: unknown): SortDir | undefined {
  return value === 'desc' ? 'desc' : undefined;
}

// ---------------------------------------------------------------------------
// Ombor qoldig'i
// ---------------------------------------------------------------------------

export interface StockSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
  readonly warehouseId?: number;
  /** `true` bo'lsagina URL da bo'ladi — default holat nol qatorlarni yashiradi. */
  readonly includeZero?: boolean;
}

export function parseStockSearch(search: Record<string, unknown>): StockSearch {
  const page = positiveInt(search.page);
  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(sortDir(search.sortDir) ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(positiveInt(search.warehouseId) !== undefined
      ? { warehouseId: positiveInt(search.warehouseId) as number }
      : {}),
    // `'true'` matni ham qabul qilinadi: havola qo'lda yozilgan bo'lishi mumkin.
    ...(search.includeZero === true || search.includeZero === 'true'
      ? { includeZero: true }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Ombor daftari
// ---------------------------------------------------------------------------

export interface MovementSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
  readonly warehouseId?: number;
  readonly productId?: number;
  readonly txnType?: InventoryTxnType;
  readonly sourceTable?: string;
  readonly from?: string;
  readonly to?: string;
}

function isTxnType(value: unknown): value is InventoryTxnType {
  return typeof value === 'string' && (INVENTORY_TXN_TYPES as readonly string[]).includes(value);
}

export function parseMovementSearch(search: Record<string, unknown>): MovementSearch {
  const page = positiveInt(search.page);
  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(sortDir(search.sortDir) ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(positiveInt(search.warehouseId) !== undefined
      ? { warehouseId: positiveInt(search.warehouseId) as number }
      : {}),
    ...(positiveInt(search.productId) !== undefined
      ? { productId: positiveInt(search.productId) as number }
      : {}),
    ...(isTxnType(search.txnType) ? { txnType: search.txnType } : {}),
    ...(text(search.sourceTable) ? { sourceTable: search.sourceTable as string } : {}),
    ...(isIsoDate(search.from) ? { from: search.from } : {}),
    ...(isIsoDate(search.to) ? { to: search.to } : {}),
  };
}

// ---------------------------------------------------------------------------
// Sotuv buyurtmalari
// ---------------------------------------------------------------------------

export interface SalesOrderSearch {
  readonly page?: number;
  readonly sortBy?: string;
  readonly sortDir?: SortDir;
  readonly q?: string;
  readonly status?: SalesOrderStatus;
  readonly customerId?: number;
  readonly hasPreorder?: boolean;
}

function isOrderStatus(value: unknown): value is SalesOrderStatus {
  return typeof value === 'string' && (SALES_ORDER_STATUS as readonly string[]).includes(value);
}

export function parseSalesOrderSearch(search: Record<string, unknown>): SalesOrderSearch {
  const page = positiveInt(search.page);
  return {
    ...(page !== undefined && page > 1 ? { page } : {}),
    ...(text(search.sortBy) ? { sortBy: search.sortBy as string } : {}),
    ...(sortDir(search.sortDir) ? { sortDir: 'desc' as SortDir } : {}),
    ...(text(search.q) ? { q: search.q as string } : {}),
    ...(isOrderStatus(search.status) ? { status: search.status } : {}),
    ...(positiveInt(search.customerId) !== undefined
      ? { customerId: positiveInt(search.customerId) as number }
      : {}),
    ...(search.hasPreorder === true || search.hasPreorder === 'true'
      ? { hasPreorder: true }
      : {}),
  };
}
